import {
	mentionPayloadFromUrl,
	mentionUrl,
} from "@ui/organisms/QuestionBox/mentionUrl";
import { inlineToHtml } from "@ui/organisms/QuestionBox/promptClipboardHtml";
import { inlineFromPayload } from "@ui/organisms/QuestionBox/questionDocument";
import type { MentionedVideoItem } from "../components/VideoMention";

/**
 * Puts a copied mention on the clipboard in the form the QuestionBox writes:
 * `<a href="https://…/mention?…">@handle</a>`, a bare anchor with a single text
 * child (see promptClipboardHtml).
 *
 * The chips as they render are a poor thing to hand to another app. An
 * account's label is a platform glyph — a ligature of the literal text
 * `tiktok:` — so the anchor wraps an element rather than text, and reads as
 * `tiktok:handle`, which a host that re-linkifies what it is given can take for
 * a `scheme:path` URI and replace with a link of its own. A video's label is a
 * citation number, which means nothing away from the answer that numbered it.
 * Slack is the case that matters: a prompt is routinely pasted into a
 * conversation and copied back out, and the QuestionBox's format is the one
 * built to survive that trip.
 *
 * So the chip is decoded back to the atom it stands for and re-serialised by
 * `inlineToHtml` — the same call the QuestionBox makes — rather than written out
 * again here. There is one definition of a mention link, and a mention copied
 * from an answer is therefore byte-identical to one copied from the input.
 *
 * Only the mentions are rewritten — the rest of the selection is the browser's
 * own copy of it, so tables, emphasis and lists come through untouched — and a
 * selection holding none of them is left alone entirely.
 */

/**
 * The chat's chips — an account mention, a video citation, a video pill in an
 * asked question, a clip's thumbnail — each of which the link stands in for.
 */
const CHIP_SELECTOR =
	".mention, .video-mention, .video-mention-pill, .mentioned-video-thumbnail";

/**
 * Where a chip that cannot be an anchor keeps its link. A clip's thumbnail is a
 * play control in the answer (and may itself contain an add button) and an
 * `<img>` in the sources list — neither is an `<a>`, so the link rides on this
 * attribute instead, which the rewrite below reads exactly as it reads an
 * `href`, so such a chip copies as the same link as the citation beside it.
 * Set by MentionedVideoThumbnail and MentionedVideosList.
 */
const LINK_ATTR = "data-mention-href";

/** The elements that may be carrying a mention link. */
const LINK_SELECTOR = `a[href], [${LINK_ATTR}]`;

/** Marks the elements the page is not showing, for the length of one clone. */
const HIDDEN_ATTR = "data-clipboard-hidden";

/** Elements that end a line when the copied fragment is read as plain text. */
const BLOCK_TAGS = new Set([
	"P",
	"DIV",
	"LI",
	"UL",
	"OL",
	"TABLE",
	"TR",
	"H1",
	"H2",
	"H3",
	"H4",
	"H5",
	"H6",
	"BLOCKQUOTE",
	"PRE",
	"SECTION",
	"ARTICLE",
]);

/**
 * The `/mention` link for a video the chat is showing, or null when the clip has
 * no addressable id. Shared by the two shapes a video takes — the numbered
 * citation in an answer and the titled pill in an asked question — so both copy
 * as the same link.
 *
 * That id is `video_id`, the source video's public id: `id` is an ephemeral
 * display id minted for the render that produced this mention, so a link built
 * on it could never resolve. Both are handed over as they are — `mentionUrl`
 * decides what it can address, and answers old enough to carry no public id at
 * all simply get no link.
 */
export const videoMentionUrl = (mention: MentionedVideoItem): string | null =>
	mentionUrl({
		kind: "pill",
		data: {
			id: mention.video_id ?? mention.id,
			// Untitled clips are left without one rather than given a made-up title
			// (or a citation number, which means nothing away from its answer); the
			// paste names them, as it does for a pill copied out of the input.
			label: mention.title ?? "",
			startTimeSec: mention.start_time_sec,
			endTimeSec: mention.end_time_sec,
		},
	});

/** The clipboard's form of the mention `link` addresses, or null if it is an ordinary link. */
function clipboardHtmlFor(link: Element): string | null {
	const href = link.getAttribute("href") ?? link.getAttribute(LINK_ATTR) ?? "";
	const payload = mentionPayloadFromUrl(href);
	const inline = payload ? inlineFromPayload(payload) : null;
	return inline ? inlineToHtml(inline) : null;
}

/**
 * Replaces every mention chip in `holder` with its clipboard form, in place.
 * Returns whether it held any, so a copy of ordinary text can be left to the
 * browser.
 */
export function rewriteMentionsForClipboard(holder: HTMLElement): boolean {
	let found = false;
	for (const link of Array.from(holder.querySelectorAll(LINK_SELECTOR))) {
		const html = clipboardHtmlFor(link);
		if (html === null) continue;
		// The chip rather than the link, so nothing of the glyph or the thumbnail
		// survives. Every node here was cloned into `holder`, so each one has an
		// element parent.
		const chip = link.closest(CHIP_SELECTOR) ?? link;
		chip.outerHTML = html;
		found = true;
	}
	return found;
}

function appendPlainText(node: Node, lines: string[]): void {
	if (node.nodeType === Node.TEXT_NODE) {
		lines[lines.length - 1] += node.textContent ?? "";
		return;
	}
	if (!(node instanceof Element)) return;
	if (node.tagName === "BR") {
		lines.push("");
		return;
	}
	const isBlock = BLOCK_TAGS.has(node.tagName);
	if (isBlock && lines[lines.length - 1].length > 0) lines.push("");
	for (const child of node.childNodes) appendPlainText(child, lines);
	if (isBlock && lines[lines.length - 1].length > 0) lines.push("");
}

/**
 * The copied fragment as readable prose. `innerText` would do this, but only
 * once the content has been laid out, and the fragment is deliberately never
 * attached to the document.
 */
export function plainTextForClipboard(holder: HTMLElement): string {
	const lines = [""];
	for (const child of holder.childNodes) appendPlainText(child, lines);
	return lines.join("\n").trim();
}

/** The element `node` is, or the one holding it. */
const elementOf = (node: Node): Element | null =>
	node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;

/** Whether `range` reaches a chip, checked before cloning anything. */
function touchesMention(range: Range): boolean {
	const element = elementOf(range.commonAncestorContainer);
	if (!element) return false;
	return (
		element.closest(CHIP_SELECTOR) !== null ||
		element.querySelector(CHIP_SELECTOR) !== null
	);
}

/**
 * The selection's markup, leaving out anything the page is not showing.
 *
 * `cloneContents` is a DOM operation, so on its own it copies elements the
 * stylesheet hides. Answers with a video carousel rely on that: a paragraph
 * whose only content was a citation marker is collapsed, because the carousel
 * beside it already stands for the citation, and the marker is left in the DOM
 * for later render passes. The browser's own copy leaves such a paragraph out,
 * and so must this one, or copying an answer would pick up citations that are
 * not on screen.
 *
 * Hidden elements are found on the live DOM — where there is layout to ask —
 * marked, and dropped from the clone. Testing `display` per element rather than
 * the whole ancestor chain is enough, since a hidden ancestor is marked in its
 * own right and takes its subtree with it.
 */
export function copiedMarkup(range: Range): HTMLElement {
	const scope = elementOf(range.commonAncestorContainer);
	const hidden = scope
		? Array.from(scope.querySelectorAll("*")).filter(
				(element) => getComputedStyle(element).display === "none",
			)
		: [];
	for (const element of hidden) element.setAttribute(HIDDEN_ATTR, "");

	const holder = document.createElement("div");
	holder.append(range.cloneContents());

	for (const element of hidden) element.removeAttribute(HIDDEN_ATTR);
	for (const element of Array.from(
		holder.querySelectorAll(`[${HIDDEN_ATTR}]`),
	)) {
		element.remove();
	}
	return holder;
}

function handleCopy(event: ClipboardEvent): void {
	// Something nearer the selection has already written the clipboard — the
	// QuestionBox writes its own two flavours — so there is nothing to correct.
	if (event.defaultPrevented) return;

	const selection = window.getSelection();
	if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;
	const range = selection.getRangeAt(0);
	if (!touchesMention(range)) return;

	const holder = copiedMarkup(range);
	if (!rewriteMentionsForClipboard(holder)) return;

	event.clipboardData?.setData("text/html", holder.innerHTML);
	event.clipboardData?.setData("text/plain", plainTextForClipboard(holder));
	event.preventDefault();
}

let installed = false;

/**
 * Starts correcting copied mentions, once per document. Listening on the
 * document rather than on a container covers every surface that renders a
 * mention — a streamed answer, a stored one, the question, the focus banner —
 * and the check above keeps it inert for every other copy in the app.
 */
export function installMentionClipboard(): void {
	if (installed || typeof document === "undefined") return;
	installed = true;
	document.addEventListener("copy", handleCopy);
}
